import { useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash';
import { SENTENCE_BUILDER_DEBOUNCE_MS } from '../utils/customPractice';

/**
 * Debounced async callback for sentence-builder preview updates.
 * Returned function has `.flush(meta)` to run immediately (blur / select).
 */
export function useDebouncedSentenceRefresh(runRefresh, deps = [], wait = SENTENCE_BUILDER_DEBOUNCE_MS) {
  const runRef = useRef(runRefresh);
  runRef.current = runRefresh;

  const depsKey = JSON.stringify(deps);
  const debouncedRef = useRef(null);
  const depsKeyRef = useRef(depsKey);

  if (!debouncedRef.current || depsKeyRef.current !== depsKey) {
    debouncedRef.current?.cancel?.();
    debouncedRef.current = debounce((meta) => {
      runRef.current(meta);
    }, wait);
    depsKeyRef.current = depsKey;
  }

  useEffect(() => {
    return () => debouncedRef.current?.cancel?.();
  }, [depsKey, wait]);

  return useMemo(() => {
    const schedule = (meta) => debouncedRef.current?.(meta);
    schedule.flush = (meta) => {
      debouncedRef.current?.cancel?.();
      if (meta != null) {
        runRef.current(meta);
      }
    };
    return schedule;
  }, [depsKey, wait]);
}
